import { useState, useEffect } from 'react'
import { getApiUrl, fetchWithAuth } from '../../../api'

interface SpaceReservationSetting {
  id: number
  daily_total_limit: number | null
}

export default function SpaceSettingsTab() {
  const [setting, setSetting] = useState<SpaceReservationSetting | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [dailyLimit, setDailyLimit] = useState<string>('')

  useEffect(() => {
    fetchSetting()
  }, [])

  const fetchSetting = async () => {
    setLoading(true)
    try {
      const res = await fetchWithAuth(getApiUrl('/marubit/api/space/admin/setting'))
      if (!res.ok) throw new Error('조회 실패')
      const data: SpaceReservationSetting = await res.json()
      setSetting(data)
      setDailyLimit(data.daily_total_limit != null ? String(data.daily_total_limit) : '')
    } catch {
      alert('설정을 불러오는 데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!window.confirm('설정을 저장하시겠습니까?')) return
    setSaving(true)
    try {
      const res = await fetchWithAuth(getApiUrl('/marubit/api/space/admin/setting'), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          daily_total_limit: dailyLimit !== '' ? Number(dailyLimit) : null,
        }),
      })
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail ?? '저장 실패') }
      const result = await res.json()
      alert(result.message ?? '저장되었습니다.')
      fetchSetting()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '저장에 실패했습니다.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-[200px] flex items-center justify-center text-gray-300 text-[13px]">
        불러오는 중...
      </div>
    )
  }

  return (
    <div className="max-w-[560px]">
      <div className="bg-white rounded-2xl border border-[#e8e0dc] px-6 py-6 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
        <h2 className="text-[14px] font-bold text-[#5a3d32] mb-1">공간 예약 설정</h2>
        <p className="text-[12px] text-gray-400 mb-6">
          인당 하루에 예약할 수 있는 공간의 총 개수를 설정합니다.
        </p>

        <div className="flex flex-col gap-1 mb-6">
          <label className="text-[12px] text-[#5a3d32] font-medium">
            1인 하루 최대 예약 가능 횟수
            <span className="text-gray-400 font-normal ml-2">(비워두면 무제한)</span>
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              value={dailyLimit}
              onChange={e => setDailyLimit(e.target.value)}
              placeholder="예: 2"
              className="border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] w-[120px] transition-colors"
            />
            <span className="text-[13px] text-gray-400">회</span>
          </div>
          {setting && (
            <p className="text-[11px] text-gray-400 mt-1">
              현재 설정:{' '}
              <span className="text-[#887267] font-semibold">
                {setting.daily_total_limit != null ? `${setting.daily_total_limit}회` : '무제한'}
              </span>
            </p>
          )}
        </div>

        <div className="border-t border-[#f5f2f0] pt-5 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#887267] text-white text-[13px] font-semibold px-6 py-2.5 rounded-lg cursor-pointer hover:bg-[#6b5248] transition-colors disabled:opacity-50 disabled:cursor-not-allowed border-none"
          >
            {saving ? '저장 중...' : '저장하기'}
          </button>
        </div>
      </div>
    </div>
  )
}